import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { ArrowRight, Users, Heart } from 'lucide-react';
import heroImage from '@/assets/greenin-americas-hero.webp';

const HeroSection = () => {
  const { t } = useLanguage();

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
      aria-labelledby="hero-heading">
      
      {/* Background with hero image */}
      <div className="absolute inset-0 z-0">
        <img
          src={heroImage}
          alt="GreenIn Impact programs — sustainability, AI for Good, and circular economy across the Americas"
          fetchPriority="high"
          decoding="sync"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
          width={1920}
          height={1080} />
        
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-transparent to-background/80" />
        {/* Glow effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center stagger-children">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 dark:bg-primary/20 border border-primary/30 text-primary text-sm font-medium mb-8">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            {t('hero.badge')}
          </div>

          {/* Main Title - Using solid color for better contrast accessibility */}
          <h1 id="hero-heading" className="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-primary">
            {t('hero.title')}
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-10 leading-relaxed pl-0 pr-0 pb-0 pt-0">
            {t('hero.subtitle')}
          </p>

          {/* CTA Buttons - Improved mobile layout */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-12 sm:mb-16 px-4 sm:px-0">
            <Button variant="hero" size="lg" className="group w-full sm:w-auto min-h-[48px]" asChild>
              <a href="#programs" aria-label={t('hero.cta1')}>
                <ArrowRight className="w-5 h-5" aria-hidden="true" />
                {t('hero.cta1')}
              </a>
            </Button>
            <div className="flex flex-row gap-3 w-full sm:w-auto">
              <Button variant="heroOutline" size="lg" className="flex-1 sm:flex-initial min-h-[48px]" asChild>
                <a href="#contact">
                  {t('hero.cta2')}
                </a>
              </Button>
              <Button variant="muted" size="lg" className="flex-1 sm:flex-initial min-h-[48px]" asChild>
                <a href="#get-involved" aria-label={t('hero.cta3')}>
                  <Heart className="w-4 h-4" aria-hidden="true" />
                  {t('hero.cta3')}
                </a>
              </Button>
            </div>
          </div>

          {/* Stats or trust indicators - Improved mobile layout */}
          <div className="flex flex-row items-center justify-center gap-4 sm:gap-8 md:gap-12 text-center">
            <div className="min-w-[70px]">
              <div className="text-xl sm:text-2xl md:text-3xl font-bold text-primary">2-5%</div>
              <div className="text-xs sm:text-sm text-muted-foreground">{t('hero.stat1')}</div>
            </div>
            <div className="w-px h-8 sm:h-10 bg-border" />
            <div className="min-w-[60px]">
              <div className="text-xl sm:text-2xl md:text-3xl font-bold text-primary">5+</div>
              <div className="text-xs sm:text-sm text-muted-foreground">{t('hero.stat2')}</div>
            </div>
            <div className="w-px h-8 sm:h-10 bg-border" />
            <div className="min-w-[70px]">
              <div className="text-xl sm:text-2xl md:text-3xl font-bold text-primary">{t('hero.stat3Value')}</div>
              <div className="text-xs sm:text-sm text-muted-foreground">{t('hero.stat3')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator - hidden from screen readers as decorative */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce" aria-hidden="true">
        <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/50 flex items-start justify-center p-2">
          <div className="w-1 h-2 rounded-full bg-primary" />
        </div>
      </div>
    </section>);

};

export default HeroSection;